import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Phone, CheckCircle, Users, Server, Brain, Globe, Wrench, Headphones, FileCheck, CreditCard, Clock, UsersRound, Building2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ConsultantChatGate from '../components/ConsultantChatGate';
import RequestCalculationForm from '../components/RequestCalculationForm';
import SEOHead from '../components/SEOHead';
import { companyInfo } from '../data/mock';
import { PAGE_SEO } from '../data/pageSeo';

const AboutPage = () => {
  const seoData = PAGE_SEO['/about'];
  const [isFormOpen, setIsFormOpen] = useState(false);

  const stats = [
    { value: '100+', label: 'Реализованных проектов' },
    { value: 'с 2017', label: 'Работаем с бизнесом' },
    { value: '3', label: 'Направления IT' },
    { value: 'SLA', label: 'Техподдержка' },
  ];

  const directions = [
    {
      icon: Globe,
      title: 'Тиражируемое программное обеспечение',
      description: 'Поставляем тиражируемое ПО на аккаунте вашей компании. Консультируем и ведём переговоры с вендорами по всему миру.',
      features: [
        'Лицензии на программное обеспечение',
        'Оплата в рублях со счёта компании',
        'Переговоры с международными вендорами',
        'Полный пакет документации',
      ],
    },
    {
      icon: Brain,
      title: 'Решения на базе искусственного интеллекта',
      description: 'Поставляем AI-продукты для автоматизации бизнес-процессов. Облачные и on-premise решения с настройкой и поддержкой.',
      features: [
        'AI-агенты и ассистенты',
        'Корпоративные базы знаний',
        'Речевая аналитика',
        'Внедрение и поддержка по SLA',
      ],
    },
    {
      icon: Server,
      title: 'Интеграция и поддержка решений',
      description: 'Интеграция ИТ-решений в вашу инфраструктуру, настройка, обучение пользователей и техническая поддержка по SLA.',
      features: [
        'Интеграция с существующими системами',
        'Настройка и внедрение',
        'Обучение сотрудников',
        'Техническая поддержка по SLA',
      ],
    },
  ];

  const advantages = [
    {
      icon: CreditCard,
      title: 'Оплата в рублях',
      description: 'Работаем только с юридическими лицами. Безналичный расчёт, полный пакет документов.',
    },
    {
      icon: Clock,
      title: 'Быстрый старт',
      description: 'Сроки поставки ПО — от 1 дня, внедрение AI-решений — от 3 дней.',
    },
    {
      icon: UsersRound,
      title: 'Персональный подход',
      description: 'Бесплатная консультация и подбор оптимального решения под ваши задачи.',
    },
    {
      icon: Building2,
      title: 'Комплексные проекты',
      description: 'Реализуем проекты для среднего и крупного бизнеса. Работаем с крупнейшими компаниями России.',
    },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEOHead
        title={seoData.title}
        description={seoData.description}
        canonical={seoData.canonical}
      />

      <Header />

      <main className="flex-1 pt-16">
        {/* Hero Section */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-accent/5 via-transparent to-transparent" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative">
            <div className="max-w-3xl">
              <h1 className="text-4xl md:text-5xl font-bold mb-6 text-black">
                О компании Антология
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed mb-3">
                Антология — поставщик лицензий и подписок на ПО для компаний в России.
                Покупка, учёт и продление — в одном сервисе: сами в личном кабинете или через
                менеджера в чате.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed mb-3">
                Мы не витрина «магазина» и не платёжный агент: помогаем юрлицам закупать и продлевать
                лицензии с оплатой в рублях и закрывающими документами. Каталог решений —{' '}
                <Link to="/solutions" className="text-accent hover:underline">
                  /solutions
                </Link>
                .
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Внедрение ИИ-агентов, RAG и on-prem — отдельный контур на{' '}
                <Link to="/services" className="text-accent hover:underline">
                  /services
                </Link>
                . С 2017 года работаем со средним и крупным бизнесом.
              </p>
            </div>
          </div>
        </section>

        {/* О компании - Сергей Успенский */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-white border border-border/50 rounded-2xl p-8 md:p-12">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">Обращение основателя</h2>
            <div className="prose prose-lg max-w-none">
              <div className="flex items-start gap-6 mb-6">
                <img 
                  src="/images/task-cta-portrait.png" 
                  alt="Сергей Успенский" 
                  className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover flex-shrink-0"
                />
                <div>
                  <p className="text-lg text-muted-foreground">
                    На связи Сергей Успенский, основатель Антология.
                  </p>
                </div>
              </div>
              <blockquote className="border-l-4 border-accent pl-6 py-4 my-6 bg-accent/5 rounded-r-lg">
                <p className="text-lg text-foreground italic mb-2">
                  Антология занимается поставкой передовых программных решений, делая особый акцент на продуктах с интеграцией ИИ.
                </p>
                <p className="text-lg text-foreground italic">
                  Наша основная работа заключается в предоставлении технологий, качественно повышающих эффективность труда. Так мы влияем на этот мир. Делаем его технологичнее, сильнее, лучше для себя и окружающих.
                </p>
              </blockquote>
              <h3 className="text-xl font-semibold mb-4 mt-8">В нашей работе мы разделяем четыре ключевых ценности по отношению к коллегам, партнерам и клиентам:</h3>
              <ol className="space-y-4 list-decimal list-inside text-muted-foreground">
                <li className="mb-4">
                  <strong className="text-foreground">Честность</strong> по отношению к себе и другим. Мы работаем в сфере информационных технологий, потому что нам это нравится. А также мы любим общение с другими людьми и влияние на мир вокруг нас.
                </li>
                <li className="mb-4">
                  <strong className="text-foreground">Энергичность</strong>. Делать свою работу хорошо — это базовая установка. Технологии стремительно меняются, и мы обязуемся меняться вслед за ними. Чтобы быть экспертом, требуется изучать новое и быть проводником этих знаний для окружающих.
                </li>
                <li className="mb-4">
                  <strong className="text-foreground">Интеллект</strong>. Для нас быть умным означает больше, чем просто оперировать информацией. Умный человек должен быть эмпатичным по отношению к окружающим. Важно слышать и быть услышанным. В нашей команде мы даём свободу в принятии решений и поддерживаем друг друга. Мы приходим на помощь коллегам и клиентам, потому что для нас важно делать добро другим людям.
                </li>
                <li className="mb-4">
                  <strong className="text-foreground">Клиентоцентричность</strong>. Ключевой ценностью для существования компании я считаю развитие отношений с клиентами. Требуется каждый раз делать всё возможное, чтобы предвосхищать ожидания клиента. Делать больше, чем клиент ожидает. При этом сохранять тёплые человеческие отношения.
                </li>
              </ol>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, idx) => (
              <Card key={idx} className="bg-card/50 border-border/50">
                <CardContent className="p-6 text-center">
                  <p className="text-3xl font-bold text-accent mb-2">{stat.value}</p>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Directions Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Направления деятельности</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Три основных направления IT-решений для автоматизации и цифровизации вашего бизнеса
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {directions.map((direction, idx) => (
              <Card key={idx} className="bg-card/50 border-border/50 hover:border-accent/30 transition-all">
                <CardContent className="p-6">
                  <div className="w-14 h-14 rounded-xl bg-accent/10 flex items-center justify-center mb-4">
                    <direction.icon className="w-7 h-7 text-accent" />
                  </div>
                  <h3 className="font-semibold text-lg mb-3">{direction.title}</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {direction.description}
                  </p>
                  <ul className="space-y-2">
                    {direction.features.map((feature, fidx) => (
                      <li key={fidx} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Process Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 bg-gradient-to-br from-accent/5 via-transparent to-transparent rounded-2xl">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Как мы работаем</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              От консультации до внедрения и поддержки — полный цикл IT-услуг
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-card/80 border-border/50">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mx-auto mb-4">
                  <Users className="w-6 h-6 text-accent" />
                </div>
                <h3 className="font-semibold mb-2">Консультация</h3>
                <p className="text-sm text-muted-foreground">Бесплатная консультация и анализ ваших задач</p>
              </CardContent>
            </Card>

            <Card className="bg-card/80 border-border/50">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mx-auto mb-4">
                  <FileCheck className="w-6 h-6 text-accent" />
                </div>
                <h3 className="font-semibold mb-2">Подбор</h3>
                <p className="text-sm text-muted-foreground">Подбираем оптимальное решение под ваши требования</p>
              </CardContent>
            </Card>

            <Card className="bg-card/80 border-border/50">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mx-auto mb-4">
                  <Wrench className="w-6 h-6 text-accent" />
                </div>
                <h3 className="font-semibold mb-2">Внедрение</h3>
                <p className="text-sm text-muted-foreground">Настройка, интеграция и обучение персонала</p>
              </CardContent>
            </Card>

            <Card className="bg-card/80 border-border/50">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mx-auto mb-4">
                  <Headphones className="w-6 h-6 text-accent" />
                </div>
                <h3 className="font-semibold mb-2">Поддержка</h3>
                <p className="text-sm text-muted-foreground">Техническая поддержка и мониторинг по SLA</p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Advantages Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Почему выбирают Антологию</h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {advantages.map((adv, idx) => (
              <Card key={idx} className="bg-card/30 border-border/30">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-4">
                    <adv.icon className="w-6 h-6 text-accent" />
                  </div>
                  <h3 className="font-semibold mb-2">{adv.title}</h3>
                  <p className="text-sm text-muted-foreground">{adv.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* What We Offer */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h3 className="text-xl font-bold mb-6 text-center">Что мы предлагаем</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {companyInfo.features.map((feature, idx) => (
              <Card key={idx} className="bg-card/30 border-border/30">
                <CardContent className="p-4 flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                  <span className="text-sm text-foreground/90">{feature}</span>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Clients Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t border-border/50">
          <h2 className="text-2xl md:text-3xl font-bold mb-12 text-center">Нам доверяют</h2>
          <div className="flex flex-wrap justify-center items-center gap-x-6 gap-y-8 md:gap-x-10 md:gap-y-10">
            {companyInfo.clients.map((client) => (
              <div
                key={client.name}
                className="flex h-7 w-[88px] items-center justify-center md:h-8 md:w-[104px]"
              >
                <img
                  src={client.logo}
                  alt={client.name}
                  className="max-h-full max-w-full object-contain opacity-70 grayscale transition-all duration-300 hover:opacity-100 hover:grayscale-0"
                />
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <Card className="bg-transparent border-accent/20">
            <CardContent className="p-8 md:p-12 text-center">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">Нужен подбор IT-решения?</h2>
              <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
                Бесплатная консультация и расчёт стоимости. Расскажем о возможностях поставки ПО, AI-продуктов или оборудования для вашей задачи.
              </p>
              <Button
                type="button"
                onClick={() => setIsFormOpen(true)}
                className="bg-accent hover:bg-accent/90 text-accent-foreground font-medium h-12 px-8"
              >
                <Phone className="w-4 h-4 mr-2" />
                Обратный звонок
              </Button>
            </CardContent>
          </Card>
        </section>
      </main>

      <Footer />
      <ConsultantChatGate />
      <RequestCalculationForm
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
      />
    </div>
  );
};

export default AboutPage;
